import { useShallow } from 'zustand/react/shallow';
import { AssetVersionCreatedListener } from '@/components/asset-version-created-listener';
import { TaskState, UploadFinishedPercentage, uploadActionMap } from '@/types/uploads';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { TaskStateBar } from '@/components/task-state-bar';
import { isDefined } from '@/utilities/guards';
import { useTranslations } from 'next-intl';

/**
 * Renders a single upload session's bar. Subscribes to *its own* session only, so
 * a per-file progress event re-renders only that bar — not every bar, and not the
 * sidebar. This is safe because `setUploadField` spreads the `uploads` record,
 * preserving the object reference of unchanged sessions, so this selector returns
 * a referentially-equal value (no re-render) when a sibling session updates.
 *
 * Props/behaviour are unchanged from the previous inline version in sidebar.tsx.
 */
const UploadTaskBar = ({
  uploadSessionId,
  open,
  hideSubTaskMenu,
}: {
  uploadSessionId: string;
  open: boolean;
  hideSubTaskMenu?: boolean;
}) => {
  const session = useUploadStore((state) => state.uploads[uploadSessionId]);
  const setUploadState = useUploadStore((state) => state.setUploadState);
  const removeUploadSession = useUploadStore((state) => state.removeUploadSession);
  const getUploadIcon = useUploadStore((state) => state.getUploadIcon);
  const openModal = useOpenModal();
  const tUpload = useTranslations('upload');

  if (!isDefined(session)) {
    return null;
  }

  const {
    uploadState,
    averageProgress,
    erroredAssets,
    filesByFolder,
    assetsUploadProgress,
    versionUploadAssetId,
    versionUploadVersionId,
    isVersionUpload,
  } = session;

  return (
    <>
      {/* Version-upload completion listener (subscribes to the PRIVATE
          asset.{id} Echo channel). Kept here, alongside the authenticated
          upload bar, rather than inside the shared TaskStateBar — which is
          also rendered on the public/guest share view, where a private
          channel must never be opened. */}
      {isDefined(versionUploadAssetId) && uploadState === TaskState.WaitingForVersion && (
        <AssetVersionCreatedListener
          assetId={versionUploadAssetId}
          versionId={versionUploadVersionId}
          uploadSessionId={uploadSessionId}
        />
      )}
      <TaskStateBar
        open={open}
        taskId={uploadSessionId}
        state={uploadState}
        progress={averageProgress}
        title={uploadActionMap[uploadState]}
        hideSubTaskMenu={hideSubTaskMenu || isVersionUpload}
        subTitle={
          uploadState === TaskState.Errored
            ? tUpload('filesFailedToUpload', { count: Object.keys(erroredAssets).length })
            : undefined
        }
        onCancel={() =>
          uploadState === TaskState.InProgress ||
          uploadState === TaskState.WaitingForVersion ||
          (uploadState === TaskState.Errored && averageProgress !== UploadFinishedPercentage)
            ? openModal(ModalId.Confirm, {
                content: tUpload('cancelUploadConfirmation'),
                onConfirm: () => setUploadState(uploadSessionId, TaskState.Cancelled),
                confirmButtonDataCy: 'cancel-upload-dialog-confirm-continue',
                dismissButtonDataCy: 'cancel-upload-dialog-confirm-dismiss',
              })
            : removeUploadSession(uploadSessionId)
        }
        disabled={uploadState === TaskState.NotStarted || Object.keys(filesByFolder).length === 0}
        subTasks={
          uploadState !== TaskState.NotStarted
            ? Object.values(filesByFolder)
                .flat()
                // Trashing an asset (from here, Asset Details, or anywhere else) flips this
                // flag via useUploadStore.setFileTrashed instead of removing the file from
                // the store, so Undo has something to restore. Hide it from the list here.
                .filter((file) => !file.trashed)
                .map((file) => {
                  const errored = erroredAssets[file.id];

                  if (errored) {
                    return {
                      subTaskId: file.id,
                      subTaskLabel: file.name,
                      subTaskDescription: errored,
                      errored: true,
                      SubTaskIcon: getUploadIcon(file.type ?? ''),
                    };
                  }
                  return {
                    subTaskId: file.id,
                    subTaskLabel: file.name,
                    subTaskProgress: assetsUploadProgress[file.id]?.uploaded
                      ? UploadFinishedPercentage
                      : (assetsUploadProgress[file.id]?.progress ?? 0),
                    SubTaskIcon: getUploadIcon(file.type ?? ''),
                    uploadSessionId: uploadSessionId,
                  };
                })
            : []
        }
      />
    </>
  );
};

/**
 * Renders one bar per upload session, in the sidebar's shared Accordion.
 * Subscribes only to the list of session *ids* (shallow-compared), so it
 * re-renders when sessions are created/removed but not on progress events —
 * uploads emit one store write per file chunk, and each UploadTaskBar handles
 * its own updates.
 */
export const UploadTaskBars = ({
  openUploadId,
  hideSubTaskMenu,
}: {
  openUploadId: string;
  hideSubTaskMenu?: boolean;
}) => {
  const uploadSessionIds = useUploadStore(useShallow((state) => Object.keys(state.uploads)));

  return (
    <>
      {uploadSessionIds.map((uploadSessionId) => (
        <UploadTaskBar
          key={uploadSessionId}
          uploadSessionId={uploadSessionId}
          open={openUploadId === uploadSessionId}
          hideSubTaskMenu={hideSubTaskMenu}
        />
      ))}
    </>
  );
};
